/** DIMSTYLE — the Dimension Style Manager, cut down to the six variables that decide a sheet.
 *
 * ⚠⚠ **A DIMSTYLE IS ~80 VARIABLES AND A DRAFTER TOUCHES SIX.** AutoCAD's manager has seven
 * tabs. The ones that decide whether a sheet is readable are the text height, the arrow size,
 * the decimal places, the separator, the linear factor and the overall scale. Showing all eighty
 * buries the six; storing only the six would silently drop the other seventy-four from a
 * client's house standard — so the editor writes these and the rest is REPLAYED untouched.
 *
 * ⚠⚠ **DIMSCALE IS THE ANNOTATIVE ONE AND IT IS THE REASON THIS DIALOG EXISTS.** A dimension is
 * drawn in MODEL units and looked at through a viewport at 1:50, so 2.5 mm text arrives on the
 * sheet at 0.05 mm. DIMSCALE multiplies every size in the style so it PRINTS at nominal size
 * through that window. The drawing's own viewport scales are offered as buttons, because
 * "which sheets does this drawing have" is a question the drawing can answer and the drafter
 * should not have to.
 */
import { useEffect, useState } from "react";

import { loadDimStyles, type DimStyleRow } from "./api";

/** ⚠ The order they appear in AutoCAD's own tabs, so a drafter's eye lands where it expects. */
const FIELDS: [string, string, "num" | "int" | "char"][] = [
  ["dimtxt", "Text height", "num"],
  ["dimasz", "Arrow size", "num"],
  ["dimdec", "Decimal places", "int"],
  ["dimdsep", "Decimal separator", "char"],
  ["dimlfac", "Linear factor", "num"],
  ["dimscale", "Overall scale (DIMSCALE)", "num"],
];

export default function DimStyleDialog({ drawingId, onApply, onClose }: {
  drawingId: number;
  onApply: (name: string, vars: Record<string, string | number>, base?: string) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<DimStyleRow[]>([]);
  const [scales, setScales] = useState<number[]>([]);
  const [pick, setPick] = useState<string>("");
  const [vals, setVals] = useState<Record<string, string>>({});
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadDimStyles(drawingId).then((j) => {
      // ⚠ Sorted by USE. A style used 40 times is the office standard and one used 0 times is
      // debris left by an import; alphabetical order puts EZ_CURVED first on every drawing.
      const sorted = [...j.styles].sort((a, b) => (b.used - a.used) || a.name.localeCompare(b.name));
      setRows(sorted);
      setScales(j.viewport_scales || []);
      if (sorted.length) choose(sorted[0]);
    }).catch(() => setErr("could not read this drawing's dimension styles"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  }, [drawingId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const choose = (r: DimStyleRow) => {
    setPick(r.name);
    setName(r.name);
    setVals(Object.fromEntries(FIELDS.map(([k]) => [k, String(r.vars[k] ?? "")])));
    setErr(null);
  };

  const go = (asNew: boolean) => {
    const n = name.trim();
    if (!n) return setErr("A dimension style needs a name.");
    const out: Record<string, string | number> = {};
    for (const [k, label, kind] of FIELDS) {
      const raw = (vals[k] ?? "").trim();
      if (!raw) continue;
      if (kind === "char") { out[k] = raw[0]; continue; }
      const v = Number(raw);
      if (!Number.isFinite(v)) return setErr(`${label} must be a number.`);
      out[k] = kind === "int" ? Math.round(v) : v;
    }
    onApply(n, out, asNew && n !== pick ? pick || "Standard" : undefined);
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 560, maxHeight: "90%", display: "flex", flexDirection: "column",
                    background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          Dimension Style Manager
        </div>
        <div style={{ padding: 12, display: "flex", gap: 12, font: "11.5px system-ui",
                      minHeight: 0 }}>
          <div style={{ width: 190, display: "flex", flexDirection: "column", gap: 4,
                        minHeight: 0 }}>
            <span style={{ color: "#9aa0a6" }}>Styles ({rows.length})</span>
            <div data-testid="dimstyle-list"
                 style={{ flex: 1, overflow: "auto", background: "#2b2d2f",
                          border: "1px solid #555", minHeight: 120, maxHeight: 260 }}>
              {rows.map((r) => (
                <div key={r.name} onClick={() => choose(r)}
                     data-testid={`dimstyle-${r.name}`}
                     style={{ padding: "3px 7px", cursor: "pointer",
                              background: r.name === pick ? "#3c5a82" : "transparent" }}>
                  {r.name}
                  {/* ⚠ The count is the point of the ordering — see the note above. */}
                  <span style={{ color: "#9aa0a6", marginLeft: 6 }}>
                    {r.used ? `· ${r.used}` : ""}
                  </span>
                </div>))}
            </div>
          </div>

          <div style={{ flex: 1, display: "grid", gap: 8, alignContent: "start" }}>
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>Name</span>
              <input value={name} data-testid="dimstyle-name" style={inp}
                     onChange={(e) => { setName(e.target.value); setErr(null); }} />
            </label>
            {FIELDS.map(([k, label]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#9aa0a6", width: 168 }}>{label}</span>
                <input value={vals[k] ?? ""} data-testid={`dimstyle-${k}`}
                       onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))}
                       style={{ ...inp, flex: 1 }} />
              </label>))}

            {scales.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                <span style={{ color: "#9aa0a6" }}>Match a viewport:</span>
                {scales.map((s) => (
                  <button key={s} data-testid={`dimstyle-vp-${s}`}
                          onClick={() => setVals((v) => ({ ...v, dimscale: String(s) }))}
                          style={{ ...btn, padding: "2px 8px" }}>1:{s}</button>))}
              </div>)}
            <div style={{ color: "#9aa0a6", fontSize: 11, lineHeight: 1.5 }}>
              ⚠ DIMSCALE multiplies text, arrows and gaps so a dimension drawn in model units
              prints at its nominal size through a viewport at that scale.
            </div>
            {err && <div data-testid="dimstyle-error" style={{ color: "#ff8f8f" }}>{err}</div>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end",
                      padding: "0 12px 12px" }}>
          <button style={btn} onClick={onClose} data-testid="dimstyle-cancel">Cancel</button>
          {/* ⚠ Two buttons, because renaming the field and pressing one button is ambiguous:
              did the drafter mean to rename this style or to make a new one from it? AutoCAD
              asks the same question with a New… button. */}
          <button style={btn} data-testid="dimstyle-new" onClick={() => go(true)}>
            New from this
          </button>
          <button style={{ ...btn, background: "#3c5a82", borderColor: "#4a6d9c" }}
                  data-testid="dimstyle-ok" onClick={() => go(false)}>Apply</button>
        </div>
      </div>
    </div>);
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "3px 6px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
